import React, { useState } from 'react';
import {
    Collapse,
    List,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    SvgIcon,
    Typography,
    Box,
    Divider,
    IconButton,
    alpha,
    useTheme,
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import mainRoutes from '../routes/mainRoutes';
import { canAccessSecgroup } from '../utils/permissions';
import { themeConstants } from '../theme/fermiTheme';
import { log } from '../utils/log';

const drawerWidth = 260;
const collapsedWidth = 58;

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    marginTop: 56,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.easeInOut,
        duration: 280,
    }),
    overflowX: 'hidden',
    backgroundColor: themeConstants.SIDEBAR_BG,
    borderRight: 'none',
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.easeInOut,
        duration: 280,
    }),
    overflowX: 'hidden',
    marginTop: 56,
    width: collapsedWidth,
    backgroundColor: themeConstants.SIDEBAR_BG,
    borderRight: 'none',
    '&:hover': {
        width: drawerWidth,
        boxShadow: '4px 0 24px rgba(0,0,0,0.15)',
    },
});

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 0.5),
    minHeight: 48,
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    }),
);

interface iSidebar {
    isAuthenticated: boolean;
    auth: any;
    drawerState?: number;
    roleName?: string | null;
}

interface iCollapseState {
    [key: string]: boolean;
}

const Sidebar = (props: iSidebar) => {
    const { isAuthenticated, auth, roleName } = props;
    const theme = useTheme();
    const location = useLocation();

    const [drawerState, setDrawerState] = useState(1);
    const [isOpen, setIsOpen] = useState(false);
    const [collapseStates, setCollapseStates] = useState<iCollapseState>({});

    const handleDrawer = () => {
        switch (drawerState) {
            case 0:
                setDrawerState(1);
                break;
            case 1:
                setDrawerState(0);
                break;
            case 2:
                setDrawerState(1);
                break;
        }
        setIsOpen(drawerState !== 0);
    };

    const changeCollapseState = (openState: string) => {
        const st: any = { ...collapseStates };
        if (!(openState in st)) {
            st[openState] = false;
        } else {
            st[openState] = !collapseStates[openState];
        }
        setCollapseStates(st);
    };

    const isActivePath = (path: string) => {
        return location.pathname === '/' + path || location.pathname.startsWith('/' + path + '/');
    };

    const isGroupActive = (pages: any[]) => {
        return pages.some((p: any) => isActivePath(p.path));
    };

    log(2, collapseStates);

    return (
        <Drawer variant="permanent" anchor="left" open={isOpen}>
            <DrawerHeader>
                <IconButton
                    onClick={handleDrawer}
                    sx={{
                        color: themeConstants.SIDEBAR_TEXT,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            color: '#fff',
                            bgcolor: alpha('#fff', 0.06),
                        },
                    }}
                >
                    {!isOpen ? <MenuIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
                </IconButton>
            </DrawerHeader>

            <Divider sx={{ borderColor: alpha('#fff', 0.06), mx: 1 }} />

            <List sx={{ px: 0.5, py: 1, flex: 1 }}>
                {mainRoutes.map((obj: any, index: number) => {
                    if (obj.type === 'noRoot') return null;

                    // @ts-ignore
                    const actual: boolean = collapseStates[obj.state];

                    // ── Collapse groups ──
                    if (obj.collapse) {
                        const accessiblePages = obj.pages.filter(
                            (p: any) => p.type !== 'noRoot' && canAccessSecgroup(roleName, p.secgroup)
                        );
                        if (accessiblePages.length === 0) return null;

                        if (!(obj.state in collapseStates)) {
                            changeCollapseState(obj.state);
                        }

                        const groupActive = isGroupActive(accessiblePages);

                        return (
                            <React.Fragment key={index}>
                                <ListItemButton
                                    onClick={() => changeCollapseState(obj.state)}
                                    sx={{
                                        minHeight: 42,
                                        borderRadius: 1.5,
                                        mx: 0.5,
                                        mb: 0.3,
                                        color: groupActive
                                            ? themeConstants.SIDEBAR_ACTIVE
                                            : themeConstants.SIDEBAR_TEXT,
                                        '&:hover': {
                                            bgcolor: alpha('#fff', 0.06),
                                            color: '#fff',
                                        },
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 36,
                                            color: 'inherit',
                                        }}
                                    >
                                        <SvgIcon
                                            component={obj.svgIconFile}
                                            sx={{ fontSize: 20 }}
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={obj.name}
                                        primaryTypographyProps={{
                                            fontSize: '0.85rem',
                                            fontWeight: groupActive ? 600 : 400,
                                        }}
                                    />
                                    {actual ? (
                                        <ExpandLess sx={{ fontSize: 18, opacity: 0.6 }} />
                                    ) : (
                                        <ExpandMore sx={{ fontSize: 18, opacity: 0.6 }} />
                                    )}
                                </ListItemButton>

                                <Collapse in={actual} timeout={200} unmountOnExit>
                                    <List disablePadding sx={{ pl: 1 }}>
                                        {accessiblePages.map((prop: any, key: number) => {
                                            if (prop.iconType === 'svg' && prop.svgIconFile) {
                                                const active = isActivePath(prop.path);
                                                return (
                                                    <ListItemButton
                                                        key={prop.name + '-' + key}
                                                        component={RouterLink}
                                                        to={prop.path}
                                                        sx={{
                                                            minHeight: 38,
                                                            borderRadius: 1.5,
                                                            mx: 0.5,
                                                            mb: 0.2,
                                                            pl: 2,
                                                            position: 'relative',
                                                            color: active
                                                                ? themeConstants.SIDEBAR_ACTIVE
                                                                : alpha('#fff', 0.55),
                                                            bgcolor: active
                                                                ? alpha(themeConstants.SIDEBAR_ACTIVE, 0.1)
                                                                : 'transparent',
                                                            '&:hover': {
                                                                bgcolor: active
                                                                    ? alpha(themeConstants.SIDEBAR_ACTIVE, 0.14)
                                                                    : alpha('#fff', 0.05),
                                                                color: active
                                                                    ? themeConstants.SIDEBAR_ACTIVE
                                                                    : '#fff',
                                                            },
                                                            '&::before': active
                                                                ? {
                                                                      content: '""',
                                                                      position: 'absolute',
                                                                      left: 0,
                                                                      top: '20%',
                                                                      height: '60%',
                                                                      width: 3,
                                                                      borderRadius: 2,
                                                                      bgcolor: themeConstants.SIDEBAR_ACTIVE,
                                                                  }
                                                                : {},
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 5,
                                                                height: 5,
                                                                borderRadius: '50%',
                                                                bgcolor: active
                                                                    ? themeConstants.SIDEBAR_ACTIVE
                                                                    : alpha('#fff', 0.25),
                                                                mr: 2,
                                                                ml: 1,
                                                                flexShrink: 0,
                                                                transition: 'all 0.2s ease',
                                                            }}
                                                        />
                                                        <ListItemText
                                                            primary={prop.name}
                                                            primaryTypographyProps={{
                                                                fontSize: '0.8rem',
                                                                fontWeight: active ? 600 : 400,
                                                            }}
                                                        />
                                                    </ListItemButton>
                                                );
                                            }
                                            return null;
                                        })}
                                    </List>
                                </Collapse>
                            </React.Fragment>
                        );
                    }

                    // ── Non-collapse items ──
                    if (!canAccessSecgroup(roleName, obj.secgroup)) return null;

                    if (obj.svgIconFile) {
                        const active = isActivePath(obj.path);
                        return (
                            <ListItemButton
                                key={obj.name + '-' + index}
                                component={RouterLink}
                                to={obj.path}
                                sx={{
                                    minHeight: 42,
                                    borderRadius: 1.5,
                                    mx: 0.5,
                                    mb: 0.3,
                                    position: 'relative',
                                    color: active
                                        ? themeConstants.SIDEBAR_ACTIVE
                                        : themeConstants.SIDEBAR_TEXT,
                                    bgcolor: active
                                        ? alpha(themeConstants.SIDEBAR_ACTIVE, 0.1)
                                        : 'transparent',
                                    '&:hover': {
                                        bgcolor: active
                                            ? alpha(themeConstants.SIDEBAR_ACTIVE, 0.14)
                                            : alpha('#fff', 0.06),
                                        color: active ? themeConstants.SIDEBAR_ACTIVE : '#fff',
                                    },
                                    '&::before': active
                                        ? {
                                              content: '""',
                                              position: 'absolute',
                                              left: 0,
                                              top: '20%',
                                              height: '60%',
                                              width: 3,
                                              borderRadius: 2,
                                              bgcolor: themeConstants.SIDEBAR_ACTIVE,
                                          }
                                        : {},
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 36,
                                        color: 'inherit',
                                    }}
                                >
                                    <SvgIcon component={obj.svgIconFile} sx={{ fontSize: 20 }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={obj.name}
                                    primaryTypographyProps={{
                                        fontSize: '0.85rem',
                                        fontWeight: active ? 600 : 400,
                                    }}
                                />
                            </ListItemButton>
                        );
                    }

                    return null;
                })}
            </List>

            {/* Footer */}
            <Box sx={{ p: 1.5, borderTop: `1px solid ${alpha('#fff', 0.06)}` }}>
                <Typography
                    variant="caption"
                    sx={{
                        color: alpha('#fff', 0.25),
                        display: 'block',
                        textAlign: 'center',
                        fontSize: '0.6rem',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                    }}
                >
                    Fermi v2
                </Typography>
            </Box>
        </Drawer>
    );
};

export default Sidebar;
